import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  getEmailMetrics,
  getEmailMetricMessages,
  getEmailMetricDetail,
  getEmailMetricTemplates,
} from '../api';
import { useTheme } from '../context/ThemeContext';

const PAGE_SIZE = 50;

function utcDayStart(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function addUtcDays(date, days) {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

export function resolveDateRange(preset, customStart = '', customEnd = '', now = new Date()) {
  if (preset === 'custom') {
    if (!customStart || !customEnd) return null;
    const start = new Date(`${customStart}T00:00:00.000Z`);
    const endExclusive = addUtcDays(new Date(`${customEnd}T00:00:00.000Z`), 1);
    if (!(start < endExclusive)) return null;
    return { startDate: start.toISOString(), endDate: endExclusive.toISOString() };
  }
  const end = now;
  const days = preset === 'last_1_month' ? 30 : 7;
  const start = utcDayStart(addUtcDays(end, -(days - 1)));
  return { startDate: start.toISOString(), endDate: end.toISOString() };
}

function dateInputsForPreset(preset, now = new Date()) {
  const range = resolveDateRange(preset === 'custom' ? 'last_7_days' : preset, '', '', now);
  return {
    customStart: range.startDate.slice(0, 10),
    customEnd: range.endDate.slice(0, 10),
  };
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('en-US');
}

function formatUtc(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', { timeZone: 'UTC', timeZoneName: 'short' });
}

function eventLabel(event) {
  if (event === 'delivered') return 'Delivered';
  if (event === 'open') return 'Opened';
  if (event === 'click') return 'Clicked';
  return event || '—';
}

function StatusMark({ ok, label, count, theme }) {
  const suffix = ok && typeof count === 'number' ? ` (${count})` : '';
  return (
    <span style={{ color: ok ? theme.success : theme.textMuted, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {ok ? '✓' : '–'} {label}{suffix}
    </span>
  );
}

function ExcludeDomainsFilter({ value, onChange, theme }) {
  const [text, setText] = useState('');

  const addDomains = () => {
    const candidates = text.split(/[\n,\s]+/).map((item) => item.trim().toLowerCase().replace(/^@/, '')).filter(Boolean);
    if (!candidates.length) return;
    const existing = new Set(value);
    const next = [...value];
    candidates.forEach((domain) => {
      if (!existing.has(domain)) {
        existing.add(domain);
        next.push(domain);
      }
    });
    onChange(next);
    setText('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '0 1 14%', minWidth: 140, maxWidth: 200 }}>
      <div style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, lineHeight: 1.2 }}>Exclude domains</div>
      <div style={{
        display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 6,
        height: 40, boxSizing: 'border-box', padding: '0 10px', overflowX: 'auto',
        border: `1px solid ${theme.inputBorder}`, borderRadius: 7, background: theme.inputBackground,
      }}>
        {value.map((domain) => (
          <span key={domain} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 7px', borderRadius: 6, background: theme.backgroundSecondary, color: theme.text, fontSize: 12, whiteSpace: 'nowrap' }}>
            {domain}
            <button
              type="button"
              aria-label={`Remove ${domain}`}
              onClick={() => onChange(value.filter((item) => item !== domain))}
              style={{ border: 0, background: 'transparent', color: theme.textSecondary, cursor: 'pointer', padding: 0, lineHeight: 1 }}
            >
              ✕
            </button>
          </span>
        ))}
        <input
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ',') {
              event.preventDefault();
              addDomains();
            } else if (event.key === 'Backspace' && !text && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={addDomains}
          placeholder={value.length ? '' : 'Domain…'}
          aria-label="Exclude domain"
          style={{ flex: '1 1 64px', minWidth: 64, height: 38, border: 0, outline: 'none', background: 'transparent', color: theme.text, fontSize: 13 }}
        />
      </div>
    </div>
  );
}

function MetricsDiagram({ metrics, loading, theme, isDarkMode, activeStatus, onSelectStatus }) {
  const blocks = [
    {
      key: 'delivered',
      value: metrics?.totalEmailsSent,
      label: 'Total Emails sent',
      width: '100%',
      maxWidth: 820,
      minHeight: 132,
      fontSize: 48,
      accent: theme.primary,
      bg: isDarkMode ? 'rgba(77,171,247,0.14)' : 'rgba(0,123,255,0.10)',
    },
    {
      key: 'open',
      value: metrics?.totalEmailsOpened,
      label: 'Total Emails opened',
      width: '78%',
      maxWidth: 640,
      minHeight: 112,
      fontSize: 40,
      accent: theme.success,
      bg: isDarkMode ? 'rgba(81,207,102,0.14)' : 'rgba(40,167,69,0.10)',
    },
    {
      key: 'click',
      value: metrics?.totalEmailsClicked,
      label: 'Total URLs clicked',
      width: '56%',
      maxWidth: 460,
      minHeight: 96,
      fontSize: 34,
      accent: theme.warning,
      bg: isDarkMode ? 'rgba(255,212,59,0.14)' : 'rgba(255,193,7,0.14)',
      sub: metrics ? `${formatNumber(metrics.totalClickEvents)} click events` : null,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, margin: '8px 0 28px' }}>
      <div style={{ width: '100%', maxWidth: 820, color: theme.textMuted, fontSize: 12, textAlign: 'center' }}>
        Click a level to filter messages by that status. Click again to clear.
      </div>
      {blocks.map((block) => {
        const active = activeStatus === block.key;
        return (
          <button
            key={block.key}
            type="button"
            onClick={() => onSelectStatus(active ? null : block.key)}
            aria-pressed={active}
            style={{
              width: block.width,
              maxWidth: block.maxWidth,
              minHeight: block.minHeight,
              borderRadius: 14,
              border: `1px solid ${active ? block.accent : theme.border}`,
              borderLeft: `6px solid ${block.accent}`,
              background: block.bg,
              boxShadow: active
                ? `0 0 0 2px ${block.accent}, 0 10px 28px ${theme.cardShadow}`
                : `0 8px 24px ${theme.cardShadow}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '18px 16px',
              boxSizing: 'border-box',
              cursor: 'pointer',
              transform: active ? 'translateY(-1px)' : 'none',
            }}
          >
            {loading ? (
              <div style={{ color: theme.textMuted }}>Loading…</div>
            ) : (
              <>
                <div style={{ fontSize: block.fontSize, fontWeight: 700, color: theme.text, lineHeight: 1.1 }}>
                  {formatNumber(block.value)}
                </div>
                <div style={{ marginTop: 8, color: theme.textSecondary, fontSize: 14, fontWeight: 600 }}>{block.label}</div>
                {block.sub ? <div style={{ marginTop: 4, color: theme.textMuted, fontSize: 12 }}>{block.sub}</div> : null}
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

function MessageDetailsModal({ messageId, onClose, theme }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getEmailMetricDetail(messageId)
      .then(({ data }) => {
        if (!active) return;
        setDetail(data);
      })
      .catch((requestError) => {
        if (active) setError(requestError.response?.data?.error || requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [messageId]);

  useEffect(() => {
    const onKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Email Events"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(860px, 100%)', maxHeight: '90vh', overflow: 'auto',
          background: theme.cardBackground, border: `1px solid ${theme.border}`,
          borderRadius: 12, boxShadow: `0 20px 48px ${theme.cardShadow}`, padding: 20,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, color: theme.text, fontSize: 20 }}>Email Events</h2>
            <div style={{ marginTop: 6, color: theme.textSecondary, fontSize: 13, wordBreak: 'break-all' }}>
              {detail?.email || '—'}
              <div>{detail?.messageId || messageId}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ border: 0, background: 'transparent', color: theme.textSecondary, fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {loading && <div style={{ color: theme.textSecondary, padding: '24px 0' }}>Loading events…</div>}
        {error && <div role="alert" style={{ color: theme.danger }}>{error}</div>}
        {!loading && !error && (
          <div style={{ overflowX: 'auto', border: `1px solid ${theme.border}`, borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Event', 'Event Timestamp', 'URL'].map((title) => (
                    <th key={title} style={{ textAlign: 'left', padding: '10px 12px', color: theme.textSecondary, background: theme.tableHeader, borderBottom: `1px solid ${theme.borderLight}` }}>{title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!detail?.events?.length ? (
                  <tr>
                    <td colSpan={3} style={{ padding: 20, textAlign: 'center', color: theme.textMuted }}>No events found.</td>
                  </tr>
                ) : detail.events.map((event, index) => (
                  <tr key={`${event.event}-${event.eventTimestamp}-${index}`} style={{ background: index % 2 ? theme.tableRowAlternate : 'transparent' }}>
                    <td style={{ padding: '10px 12px', color: theme.text, borderBottom: `1px solid ${theme.borderLight}` }}>{eventLabel(event.event)}</td>
                    <td style={{ padding: '10px 12px', color: theme.text, borderBottom: `1px solid ${theme.borderLight}` }}>{formatUtc(event.eventTimestamp)}</td>
                    <td style={{ padding: '10px 12px', color: theme.text, borderBottom: `1px solid ${theme.borderLight}`, wordBreak: 'break-all' }}>{event.url || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default function EmailMetrics() {
  const { theme, isDarkMode } = useTheme();
  const initialDates = dateInputsForPreset('last_7_days');
  const [datePreset, setDatePreset] = useState('last_7_days');
  const [customStart, setCustomStart] = useState(initialDates.customStart);
  const [customEnd, setCustomEnd] = useState(initialDates.customEnd);
  const [excludeDomains, setExcludeDomains] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [templates, setTemplates] = useState([]);
  const appliedFilters = useRef({
    datePreset: 'last_7_days',
    customStart: initialDates.customStart,
    customEnd: initialDates.customEnd,
    excludeDomains: [],
    templateId: '',
    statusFilter: null,
  });

  const onPresetChange = (preset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      const dates = dateInputsForPreset(preset);
      setCustomStart(dates.customStart);
      setCustomEnd(dates.customEnd);
    }
  };

  const onStartDateChange = (value) => {
    setCustomStart(value);
    setDatePreset('custom');
  };

  const onEndDateChange = (value) => {
    setCustomEnd(value);
    setDatePreset('custom');
  };

  const [metrics, setMetrics] = useState(null);
  const [messages, setMessages] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);

  const [metricsLoading, setMetricsLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const requestId = useRef(0);

  const inputStyle = {
    padding: '9px 12px',
    border: `1px solid ${theme.inputBorder}`,
    borderRadius: 7,
    background: theme.inputBackground,
    color: theme.text,
    height: 40,
    boxSizing: 'border-box',
  };

  const fieldStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    color: theme.textSecondary,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.2,
  };

  useEffect(() => {
    getEmailMetricTemplates()
      .then(({ data }) => setTemplates(data.templates || []))
      .catch(() => setTemplates([]));
  }, []);

  const buildPayload = (filters, nextCursor = null) => {
    const range = resolveDateRange(filters.datePreset, filters.customStart, filters.customEnd);
    if (!range) throw new Error('Select a valid custom date range.');
    return {
      ...range,
      excludeDomains: filters.excludeDomains,
      templateId: filters.templateId || null,
      statusFilter: filters.statusFilter || null,
      limit: PAGE_SIZE,
      cursor: nextCursor,
    };
  };

  const loadMessages = useCallback(async (filters, nextCursor = null, { append = false } = {}) => {
    const payload = buildPayload(filters, nextCursor);
    const response = await getEmailMetricMessages(payload);
    if (append) {
      setMessages((prev) => [...prev, ...(response.data.items || [])]);
    } else {
      setMessages(response.data.items || []);
    }
    setCursor(response.data.nextCursor || null);
    setHasMore(Boolean(response.data.hasMore));
  }, []);

  const loadData = useCallback(async (filters = appliedFilters.current) => {
    const id = ++requestId.current;
    appliedFilters.current = filters;
    setStatusFilter(filters.statusFilter || null);
    setError(null);
    setMetricsLoading(true);
    setMessagesLoading(true);
    setMessages([]);
    setCursor(null);
    setHasMore(false);

    let payload;
    try {
      payload = buildPayload(filters, null);
    } catch (buildError) {
      setError(buildError.message);
      setMetricsLoading(false);
      setMessagesLoading(false);
      return;
    }

    try {
      const [metricsResponse] = await Promise.all([
        getEmailMetrics({
          startDate: payload.startDate,
          endDate: payload.endDate,
          excludeDomains: payload.excludeDomains,
          templateId: payload.templateId,
        }),
        loadMessages(filters),
      ]);
      if (id !== requestId.current) return;
      setMetrics(metricsResponse.data);
    } catch (requestError) {
      if (id !== requestId.current) return;
      setMetrics(null);
      setMessages([]);
      setError(requestError.response?.data?.error || requestError.message || 'Unable to load email metrics.');
    } finally {
      if (id === requestId.current) {
        setMetricsLoading(false);
        setMessagesLoading(false);
      }
    }
  }, [loadMessages]);

  useEffect(() => { loadData(); }, [loadData]);

  const applyFilters = () => {
    loadData({
      datePreset,
      customStart,
      customEnd,
      excludeDomains,
      templateId,
      statusFilter,
    });
  };

  const resetFilters = () => {
    const dates = dateInputsForPreset('last_7_days');
    const defaults = {
      datePreset: 'last_7_days',
      customStart: dates.customStart,
      customEnd: dates.customEnd,
      excludeDomains: [],
      templateId: '',
      statusFilter: null,
    };
    setDatePreset(defaults.datePreset);
    setCustomStart(defaults.customStart);
    setCustomEnd(defaults.customEnd);
    setExcludeDomains(defaults.excludeDomains);
    setTemplateId(defaults.templateId);
    setStatusFilter(null);
    loadData(defaults);
  };

  const selectStatus = async (nextStatus) => {
    const nextFilters = {
      ...appliedFilters.current,
      statusFilter: nextStatus,
    };
    appliedFilters.current = nextFilters;
    setStatusFilter(nextStatus);
    setMessagesLoading(true);
    setError(null);
    setMessages([]);
    setCursor(null);
    setHasMore(false);
    try {
      await loadMessages(nextFilters);
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to load messages.');
    } finally {
      setMessagesLoading(false);
    }
  };

  const loadMore = async () => {
    if (!hasMore || loadingMore || !cursor) return;
    setLoadingMore(true);
    setError(null);
    try {
      await loadMessages(appliedFilters.current, cursor, { append: true });
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to load more messages.');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div style={{ padding: '24px 28px 48px' }}>
      <h1 style={{ margin: '0 0 6px', color: theme.text }}>Email Metrics</h1>
      <p style={{ margin: '0 0 20px', color: theme.textSecondary }}>Track delivered, opened, and clicked email activity from ClickHouse.</p>

      <div style={{
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 16,
        padding: 16,
        border: `1px solid ${theme.border}`,
        borderRadius: 12,
        background: theme.cardBackground,
        overflowX: 'auto',
      }}>
        <label style={{ ...fieldStyle, flex: '0 1 12%', minWidth: 120 }}>
          Date range
          <select value={datePreset} onChange={(event) => onPresetChange(event.target.value)} style={{ ...inputStyle, width: '100%' }}>
            <option value="last_7_days">Last 7 Days</option>
            <option value="last_1_month">Last 1 Month</option>
            <option value="custom">Custom</option>
          </select>
        </label>

        <label style={{ ...fieldStyle, flex: '0 1 12%', minWidth: 130 }}>
          Start date
          <input type="date" value={customStart} onChange={(event) => onStartDateChange(event.target.value)} style={{ ...inputStyle, width: '100%' }} />
        </label>

        <label style={{ ...fieldStyle, flex: '0 1 12%', minWidth: 130 }}>
          End date
          <input type="date" value={customEnd} onChange={(event) => onEndDateChange(event.target.value)} style={{ ...inputStyle, width: '100%' }} />
        </label>

        <label style={{ ...fieldStyle, flex: '0 1 18%', minWidth: 160 }}>
          Template
          <select value={templateId} onChange={(event) => setTemplateId(event.target.value)} style={{ ...inputStyle, width: '100%' }}>
            <option value="">All templates</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.version ? `${template.name} (${template.version})` : template.name}
              </option>
            ))}
          </select>
        </label>

        <ExcludeDomainsFilter value={excludeDomains} onChange={setExcludeDomains} theme={theme} />

        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flex: '0 0 auto', height: 62 }}>
          <button
            type="button"
            onClick={applyFilters}
            disabled={metricsLoading || messagesLoading}
            style={{
              height: 40, padding: '0 16px', border: 0, borderRadius: 7, fontWeight: 600, cursor: 'pointer',
              background: theme.primary, color: '#fff', opacity: (metricsLoading || messagesLoading) ? 0.7 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            Apply filters
          </button>
          <button
            type="button"
            onClick={resetFilters}
            style={{ height: 40, border: 0, background: 'transparent', color: theme.primary, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Reset
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" style={{ color: theme.danger, marginBottom: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <span>{error}</span>
          <button type="button" onClick={loadData} style={{ border: `1px solid ${theme.border}`, borderRadius: 6, background: theme.inputBackground, color: theme.text, cursor: 'pointer', padding: '4px 10px' }}>
            Try again
          </button>
        </div>
      )}

      <MetricsDiagram
        metrics={metrics}
        loading={metricsLoading}
        theme={theme}
        isDarkMode={isDarkMode}
        activeStatus={statusFilter}
        onSelectStatus={selectStatus}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h2 style={{ margin: 0, color: theme.text, fontSize: 18 }}>
          Messages
          {statusFilter ? (
            <span style={{ marginLeft: 10, color: theme.textSecondary, fontSize: 13, fontWeight: 500 }}>
              filtered by {statusFilter === 'delivered' ? 'sent' : statusFilter === 'open' ? 'opened' : 'clicked'}
            </span>
          ) : null}
        </h2>
        <span style={{ color: theme.textSecondary, fontSize: 13 }}>
          {messagesLoading ? 'Loading…' : `${messages.length} loaded`}
        </span>
      </div>

      <div style={{ border: `1px solid ${theme.border}`, borderRadius: 10, overflow: 'hidden', background: theme.cardBackground }}>
        {messagesLoading && !messages.length ? (
          <div style={{ padding: 40, textAlign: 'center', color: theme.textSecondary }}>Loading messages…</div>
        ) : !messages.length ? (
          <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>No email data found for the selected filters.</div>
        ) : (
          <div>
            {messages.map((row, index) => (
              <button
                key={row.messageId || index}
                type="button"
                onClick={() => setSelectedMessageId(row.messageId)}
                style={{
                  width: '100%', textAlign: 'left', border: 0, borderBottom: `1px solid ${theme.borderLight}`,
                  background: index % 2 ? theme.tableRowAlternate : 'transparent',
                  padding: '14px 16px', cursor: 'pointer',
                  display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'space-between',
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ color: theme.text, fontWeight: 600, wordBreak: 'break-all' }}>{row.email || '—'}</div>
                  <div style={{ color: theme.primary, fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>{row.messageId}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 14, flexShrink: 0 }}>
                  <StatusMark ok={row.delivered} label="Delivered" theme={theme} />
                  <StatusMark ok={row.opened} label="Opened" count={row.openedCount} theme={theme} />
                  <StatusMark ok={row.clicked} label="Clicked" count={row.clickedCount} theme={theme} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {hasMore && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            style={{
              padding: '10px 18px', border: `1px solid ${theme.border}`, borderRadius: 7,
              background: theme.backgroundSecondary, color: theme.text, fontWeight: 600,
              cursor: loadingMore ? 'not-allowed' : 'pointer',
            }}
          >
            {loadingMore ? 'Loading…' : 'Load More'}
          </button>
        </div>
      )}

      {selectedMessageId && (
        <MessageDetailsModal
          messageId={selectedMessageId}
          onClose={() => setSelectedMessageId(null)}
          theme={theme}
        />
      )}
    </div>
  );
}
